import { useTranslation } from 'react-i18next';

import { DEFAULT_VALUES } from '@/components/Filters/Filters.constants';
import { FilterTypeEnum } from '@/constants/common';
import { FormValuesKeys } from '@/types';

type Props = {
  formField: FormValuesKeys | FormValuesKeys[];
  fieldValue: unknown;
  type: FilterTypeEnum;
};

type PreparedDataReturnType = {
  hasValue: boolean;
  selectedValueText: string;
};

export const useFilterPreparedData = ({
  formField,
  fieldValue,
  type
}: Props): PreparedDataReturnType => {
  const { t } = useTranslation();

  const getDefaultValue = (key: string) =>
    DEFAULT_VALUES?.[key as keyof typeof DEFAULT_VALUES] ?? null;

  const getRangePreparedData = (): PreparedDataReturnType | undefined => {
    if (!Array.isArray(formField)) return;

    const [firstFormField, secondFormFiled] = formField;
    const [firstValue, secondValue] = Array.isArray(fieldValue)
      ? fieldValue
      : [];

    const defaultValues = {
      from: getDefaultValue(firstFormField),
      to: getDefaultValue(secondFormFiled)
    };

    const isSalaryChanged = {
      from:
        typeof defaultValues.from === 'number' &&
        firstValue !== defaultValues.from,
      to:
        typeof defaultValues.to === 'number' && secondValue !== defaultValues.to
    };

    return {
      hasValue: !!isSalaryChanged.from || !!isSalaryChanged.to,
      selectedValueText: `${t('Filter.from')} ${firstValue} ${t('Filter.to')}  ${secondValue} ₼`
    };
  };

  const getMultiplePreparedData = (): PreparedDataReturnType | undefined => {
    const value = Array.isArray(fieldValue) ? fieldValue : [];
    const valueLength = value.length;

    return {
      hasValue: valueLength > 0,
      selectedValueText:
        valueLength > 1
          ? `${valueLength} ${t('Filter.selected')}`
          : value?.[0]?.name
    };
  };

  const getDefaultPreparedData = (): PreparedDataReturnType => {
    const hasValue = fieldValue !== null && fieldValue !== undefined;
    const computedFieldValue = fieldValue as { name: string };

    return {
      hasValue,
      selectedValueText: computedFieldValue?.name
    };
  };

  const prepareData = [
    type === FilterTypeEnum.RANGE && getRangePreparedData,
    type === FilterTypeEnum.MULTIPLE && getMultiplePreparedData
  ].filter(Boolean)[0];

  return (prepareData ? prepareData?.() : null) ?? getDefaultPreparedData();
};
